import { computed, Injectable, signal } from '@angular/core';
import { STORAGE_KEYS } from '../utils/consts';
import { DepartmentSelection } from '../store/stores.models';

/**
 * Visual representation for read-only inventory lists and form item sections.
 * - `list`: vertical list with UPI drill-down
 * - `table`: tabular layout (simple or form-specific columns)
 */
export type InventoryView = 'list' | 'table';

/** @deprecated Use {@link InventoryView} */
export type FormItemsView = InventoryView;

export type InventoryViewInstanceId =
  | 'my-items'
  | 'all-inventory'
  | 'forms'
  | 'manage-user-inventory-dialog';

export interface InventoryViewOption {
  value: InventoryView;
  labelKey: string;
}

const DEFAULT_INVENTORY_VIEW: InventoryView = 'table';

const INVENTORY_VIEW_OPTIONS: readonly InventoryViewOption[] = [
  { value: 'list', labelKey: 'settings.inventory-view.list' },
  { value: 'table', labelKey: 'settings.inventory-view.table' },
];

type InstanceOverrides = Partial<Record<InventoryViewInstanceId, InventoryView>>;

/** Scope of items shown on the my-items page: only the signed-in user's own items, or everyone in their department. */
export type MyItemsScope = 'mine' | 'department';

const DEFAULT_MY_ITEMS_SCOPE: MyItemsScope = 'department';

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly inventoryViewSignal = signal<InventoryView>(
    this.readInventoryView()
  );

  private readonly instanceOverridesSignal = signal<InstanceOverrides>(
    this.readInstanceOverrides()
  );

  private readonly myItemsScopeSignal = signal<MyItemsScope>(
    this.readMyItemsScope()
  );

  private readonly myItemsDepartmentSignal = signal<DepartmentSelection | null>(
    this.readMyItemsDepartment()
  );

  readonly myItemsScope = computed(() => this.myItemsScopeSignal());

  /**
   * Department the my-items "department" scope is pointed at, for admins /
   * warehouse managers who picked one explicitly. `null` means the signed-in
   * user's own department, which is what every other role always gets.
   */
  readonly myItemsDepartment = computed(() => this.myItemsDepartmentSignal());

  readonly inventoryView = computed(() => this.inventoryViewSignal());

  /** @deprecated Use {@link inventoryView} */
  readonly formItemsView = this.inventoryView;

  readonly availableInventoryViews = INVENTORY_VIEW_OPTIONS;

  /** @deprecated Use {@link availableInventoryViews} */
  readonly availableFormItemsViews = this.availableInventoryViews;

  effectiveView(instanceId: InventoryViewInstanceId): InventoryView {
    return this.instanceOverridesSignal()[instanceId] ?? this.inventoryView();
  }

  setInventoryView(view: InventoryView): void {
    if (!this.isSupportedInventoryView(view)) {
      return;
    }
    this.inventoryViewSignal.set(view);
    try {
      localStorage.setItem(STORAGE_KEYS.INVENTORY_VIEW, view);
      localStorage.setItem(STORAGE_KEYS.FORM_ITEMS_VIEW, view);
    } catch (error) {
      console.error('Failed to persist inventory view preference:', error);
    }
  }

  /** @deprecated Use {@link setInventoryView} */
  setFormItemsView(view: InventoryView): void {
    this.setInventoryView(view);
  }

  setInstanceOverride(
    instanceId: InventoryViewInstanceId,
    view: InventoryView | null
  ): void {
    const next = { ...this.instanceOverridesSignal() };
    if (view === null) {
      delete next[instanceId];
    } else if (this.isSupportedInventoryView(view)) {
      next[instanceId] = view;
    } else {
      return;
    }
    this.instanceOverridesSignal.set(next);
    try {
      if (Object.keys(next).length === 0) {
        localStorage.removeItem(STORAGE_KEYS.INVENTORY_VIEW_OVERRIDES);
      } else {
        localStorage.setItem(
          STORAGE_KEYS.INVENTORY_VIEW_OVERRIDES,
          JSON.stringify(next)
        );
      }
    } catch (error) {
      console.error('Failed to persist inventory view override:', error);
    }
  }

  isSupportedInventoryView(value: unknown): value is InventoryView {
    return value === 'list' || value === 'table';
  }

  setMyItemsScope(scope: MyItemsScope): void {
    if (!this.isSupportedMyItemsScope(scope)) {
      return;
    }
    this.myItemsScopeSignal.set(scope);
    try {
      localStorage.setItem(STORAGE_KEYS.MY_ITEMS_SCOPE, scope);
    } catch (error) {
      console.error('Failed to persist my-items scope preference:', error);
    }
  }

  isSupportedMyItemsScope(value: unknown): value is MyItemsScope {
    return value === 'mine' || value === 'department';
  }

  setMyItemsDepartment(selection: DepartmentSelection | null): void {
    this.myItemsDepartmentSignal.set(selection);
    try {
      if (selection) {
        localStorage.setItem(
          STORAGE_KEYS.MY_ITEMS_DEPARTMENT,
          JSON.stringify(selection)
        );
      } else {
        localStorage.removeItem(STORAGE_KEYS.MY_ITEMS_DEPARTMENT);
      }
    } catch (error) {
      console.error('Failed to persist my-items department preference:', error);
    }
  }

  /** @deprecated Use {@link isSupportedInventoryView} */
  isSupportedFormItemsView(value: unknown): value is InventoryView {
    return this.isSupportedInventoryView(value);
  }

  private readInventoryView(): InventoryView {
    try {
      const stored =
        localStorage.getItem(STORAGE_KEYS.INVENTORY_VIEW) ??
        localStorage.getItem(STORAGE_KEYS.FORM_ITEMS_VIEW);
      if (this.isSupportedInventoryView(stored)) {
        return stored;
      }
    } catch (error) {
      console.error('Failed to read inventory view preference:', error);
    }
    return DEFAULT_INVENTORY_VIEW;
  }

  private readInstanceOverrides(): InstanceOverrides {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.INVENTORY_VIEW_OVERRIDES);
      if (!raw) {
        return {};
      }
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) {
        return {};
      }
      const result: InstanceOverrides = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (
          this.isInventoryViewInstanceId(key) &&
          this.isSupportedInventoryView(value)
        ) {
          result[key] = value;
        }
      }
      return result;
    } catch (error) {
      console.error('Failed to read inventory view overrides:', error);
      return {};
    }
  }

  private readMyItemsScope(): MyItemsScope {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.MY_ITEMS_SCOPE);
      if (this.isSupportedMyItemsScope(stored)) {
        return stored;
      }
    } catch (error) {
      console.error('Failed to read my-items scope preference:', error);
    }
    return DEFAULT_MY_ITEMS_SCOPE;
  }

  private readMyItemsDepartment(): DepartmentSelection | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.MY_ITEMS_DEPARTMENT);
      if (!raw) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof (parsed as DepartmentSelection).departmentId === 'string' &&
        (parsed as DepartmentSelection).departmentId.length > 0
      ) {
        const { departmentId, subDepartmentId } = parsed as DepartmentSelection;
        return {
          departmentId,
          ...(typeof subDepartmentId === 'string' ? { subDepartmentId } : {}),
        };
      }
    } catch (error) {
      console.error('Failed to read my-items department preference:', error);
    }
    return null;
  }

  private isInventoryViewInstanceId(
    value: string
  ): value is InventoryViewInstanceId {
    return (
      value === 'my-items' ||
      value === 'all-inventory' ||
      value === 'forms' ||
      value === 'manage-user-inventory-dialog'
    );
  }
}
